"use client"

import React, { useState, useMemo, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import Breadcrumb from "../../utils/breadcrumbs"
import { FiEye, FiTrash2, FiPlus } from "react-icons/fi"
import formatDate from "../../utils/format-date"
import ConfirmationModal from "../../ui/confirmation-modal"
import toast from "react-hot-toast"
import api from "../../libs/axios"
import Pagination from "../../utils/pagination2"
import { ListLoader } from "../../utils/loader"

export enum TournamentStatus {
  DRAFT = "draft",
  REGISTRATION_OPEN = "registration_open",
  REGISTRATION_CLOSED = "registration_closed",
  ONGOING = "ongoing",
  COMPLETED = "completed",
  CANCELLED = "cancelled",
}

export enum TournamentCategory {
  OPEN = "open",
  CORPORATE = "corporate",
}

export interface ITournament {
  id: number
  name: string
  description?: string
  organizer_id: number
  tournament_category: TournamentCategory
  status: TournamentStatus
  start_date: string
  end_date?: string
  registration_deadline?: string
  max_teams?: number
  entry_fee?: string
  prize_pool?: string
  location?: string
  banner_url?: string
  tournament_logo_url?: string
  last_batter_rule: boolean
  pitch_type: string
  ball_type: string
  ground_id: string
  more_teams: boolean
  match_officials: boolean
  match_type?: string
  rules?: string
  created_at?: string
  updated_at?: string
  organizer: {
    id: number
    phone_number: string
    role_id: number
    details: {
      first_name: string
      last_name: string
    }
  }
  ground: {
    id: number
    name: string
    value: string
    description: string
  }
}


interface IPagination {
  current_page: number;
  per_page: number;
  total: number;
  total_pages: number;
  has_next_page: boolean;
  has_prev_page: boolean;
  next_page: number | null;
  prev_page: number | null;
}

const Tournaments: React.FC = () => {
  const navigate = useNavigate()
  const [selectedTournament, setSelectedTournament] = useState<ITournament | null>(null)
  const [searchParams, setSearchParams] = useState<Record<string, string>>({})
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [tournaments, setTournaments] = useState<ITournament[]>([])
  const [loading, setLoading] = useState(false)
  const [limit, setLimit] = useState(10)
  const [pagination, setPagination] = useState<IPagination>({
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 1,
    has_next_page: false,
    has_prev_page: false,
    next_page: null,
    prev_page: null
  });

  // Filter tournaments based on search parameters (client-side filtering)
  const filteredTournaments = useMemo(() => {
    return tournaments.filter((tournament) => {
      const nameMatch = !searchParams.search || tournament.name.toLowerCase().includes(searchParams.search.toLowerCase())

      const categoryMatch =
        !searchParams.category ||
        tournament.tournament_category.toLowerCase() === searchParams.category.toLowerCase()

      const statusMatch = !searchParams.status || tournament.status.toLowerCase() === searchParams.status.toLowerCase()

      return nameMatch && categoryMatch && statusMatch
    })
  }, [tournaments, searchParams])

  // Fetch tournaments with search parameters
  const fetchTournaments = async (useSearchParams = false) => {
    try {
      setLoading(true)
      let url = `/tournaments?page=${pagination.current_page}&limit=${limit}`

      // Add search parameters to API call if they exist and useSearchParams is true
      if (useSearchParams && Object.keys(searchParams).length > 0) {
        const params = new URLSearchParams()
        if (searchParams.search) params.append('search', searchParams.search)
        if (searchParams.category) params.append('category', searchParams.category)
        if (searchParams.status) params.append('status', searchParams.status)
        url += `&${params.toString()}`
      }

      const response = await api.get(url)
      setTournaments(response.data.data)
      setPagination(response.data.pagination)
      setLoading(false)
    } catch (error) {
      console.log(error)
      setLoading(false)
    }
  }

  // Fetch tournaments on page/limit change
  useEffect(() => {
    fetchTournaments()
  }, [pagination.current_page, limit, searchParams?.status, searchParams?.category])

  // Search in database when filtered list is empty but search params exist
  useEffect(() => {
    if (Object.keys(searchParams).length > 0 && filteredTournaments.length === 0 && !loading) {
      fetchTournaments(true)
    }
  }, [filteredTournaments.length, searchParams])

  // Handle search
  const handleSearchChange = (column: string, value: string) => {
    setSearchParams((prev) => {
      const newParams = { ...prev }
      if (value) {
        // For name search, use 'search' parameter as per controller
        if (column === 'name') {
          newParams['search'] = value
        } else {
          newParams[column] = value
        }
      } else {
        // Remove the parameter when cleared
        if (column === 'name') {
          delete newParams['search']
        } else {
          delete newParams[column]
        }
      }
      return newParams
    })
    setPagination(prev => ({ ...prev, current_page: 1 }))

    // If search is cleared (empty value), fetch fresh data from API
    if (!value) {
      fetchTournaments(false)
    }
  }

  // Handle limit change
  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  // Get status style
  const getStatusStyle = (status: TournamentStatus) => {
    switch (status.toLowerCase()) {
      case "ongoing":
        return "bg-red-100 text-red-800"
      case "completed":
        return "bg-green-100 text-green-800"
      case "registration_open":
        return "bg-blue-100 text-blue-800"
      case "registration_closed":
        return "bg-yellow-100 text-yellow-800"
      case "draft":
        return "bg-gray-100 text-gray-800"
      case "cancelled":
        return "bg-red-100 text-red-600"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  // Handle page change
  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, current_page: page }));
  };

  // Handle delete
  const handleDeleteClick = (tournament: ITournament) => {
    setSelectedTournament(tournament)
    setDeleteConfirmOpen(true)
  }

  // Handle confirm delete
  const handleDeleteConfirm = async () => {
    if (selectedTournament) {
      try {
        await api.delete(`/tournaments/${selectedTournament.id}`)
        toast.success(`Tournament deleted successfully!`)
        fetchTournaments();
      } catch (err) {
        toast.error("Error deleting tournament")
      }
      setDeleteConfirmOpen(false)
      setSelectedTournament(null)
    }
  }

  return (
    <div className="w-full min-h-screen bg-white p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-primary-950 flex items-center">
          <span className="bg-primary-700 w-1 h-6 mr-3 rounded"></span>
          Tournaments
        </h2>
        <Breadcrumb />
      </div>
      <div className="w-full bg-white rounded-lg shadow border">
        <div className="flex justify-end p-4 w-full rounded-xl sticky top-0 bg-white">
          <Link
            to="/tournaments/create"
            className="flex gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg items-center sm:px-4"
          >
            <FiPlus className="w-5 h-5 sm:mr-0" />
            <span className="hidden sm:block">Add Tournament</span>
          </Link>
        </div>

        <div className="overflow-x-auto min-h-[70vh]">
          <table className="w-full min-w-full divide-y divide-gray-200 border border-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-0">
              <tr>
                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                  Tournament Name
                </th>
                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                  Category
                </th>
                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                  Date & Time
                </th>
                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                  Status
                </th>
                {/* <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                  Prize/Fee Info
                </th> */}
                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr className="bg-gray-50">
                <td className="px-6 py-2 border-r">
                  <input
                    type="text"
                    placeholder="Search Name"
                    value={searchParams.search || ""}
                    className="w-full p-2 h-10 border rounded text-sm"
                    onChange={(e) => handleSearchChange("name", e.target.value)}
                  />
                </td>
                <td className="px-6 py-2 border-r">
                  <select
                    value={searchParams.category || ""}
                    className="w-full p-2 h-10 border rounded text-sm"
                    onChange={(e) => handleSearchChange("category", e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="open">Open</option>
                    <option value="corporate">Corporate</option>
                    <option value="community">Community</option>
                    <option value="school">School</option>
                    <option value="college">College</option>
                    <option value="university">University</option>
                    <option value="series">Series</option>
                    <option value="other">Other</option>
                  </select>
                </td>
                <td className="px-6 py-2 border-r"></td>
                <td className="px-6 py-2 border-r">
                  <select
                    value={searchParams.status || ""}
                    className="w-full p-2 h-10 border rounded text-sm"
                    onChange={(e) => handleSearchChange("status", e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="draft">Draft</option>
                    <option value="registration_open">Registration Open</option>
                    <option value="registration_closed">Registration Closed</option>
                    <option value="ongoing">Ongoing</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </td>
                <td className="px-6 py-2 border-r"></td>
                {/* <td className="px-6 py-2 border-r"></td> */}
              </tr>
              {loading ? (
                <ListLoader />
              ) : filteredTournaments.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-gray-500">
                    No tournaments found
                  </td>
                </tr>
              ) : (
                filteredTournaments.map((tournament) => (
                  <tr
                    key={tournament.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate("/tournaments/view", { state: { tournamentId: tournament.id } })}
                  >
                    <td className="px-6 py-4 whitespace-nowrap border-r">
                      <div className="flex font-semibold items-center">{tournament.name}</div>
                      <div className="text-sm text-gray-500">{tournament.location || "Location TBD"}</div>
                      <div className="text-xs text-gray-400">
                        Organizer: {tournament.organizer?.details?.first_name} {tournament.organizer?.details?.last_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap border-r">
                      <div className="font-medium capitalize">{tournament.tournament_category}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap border-r text-sm">
                      <div>{formatDate(tournament.start_date)}</div>
                      {tournament.end_date && (
                        <div className="text-gray-500">to {formatDate(tournament.end_date)}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap border-r capitalize">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusStyle(tournament.status)}`}>
                        {tournament.status.replace(/_/g, " ")}
                      </span>
                    </td>
                    {/* <td className="px-6 py-4 whitespace-nowrap border-r text-sm">
                      <div className="font-medium">
                        {tournament.prize_pool && parseFloat(tournament.prize_pool) > 0
                          ? `₹${parseFloat(tournament.prize_pool).toLocaleString()}`
                          : "No prize"}
                      </div>
                      <div className="text-gray-500">
                        Entry: {tournament.entry_fee && parseFloat(tournament.entry_fee) > 0
                          ? `₹${parseFloat(tournament.entry_fee)}`
                          : "Free"}
                      </div>
                    </td> */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex space-x-3" onClick={(e) => e.stopPropagation()}>
                        <Link to={`/tournaments/view`} state={{ tournamentId: tournament.id }} className="text-primary-600 hover:text-primary-700">
                          <FiEye className="w-5 h-5" />
                        </Link>
                        <button
                          onClick={() => handleDeleteClick(tournament)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <FiTrash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex w-full bg-white sticky rounded-lg bottom-0">
          <Pagination
            pagination={pagination}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
          />
        </div>

        {deleteConfirmOpen && (
          <ConfirmationModal
            isOpen={deleteConfirmOpen}
            onClose={() => setDeleteConfirmOpen(false)}
            onConfirm={handleDeleteConfirm}
            title="Delete Tournament"
            description="Are you sure you want to delete this tournament?"
            loading={loading}
          />
        )}
      </div>
    </div>
  )
}

export default Tournaments